import { useContext, useEffect, useState, useMemo } from "react";
import { usePost } from "./API";
import { snakeToEnglish } from "./Util";
import { useLocalizedStrings } from "./Localization";
import InterfaceContext, { PublicConfigurationContext } from "./Context";
import { Text, interfaceNumber, ApplicantIdentities as ApplicantIdentitiesConfig, ValueExpr, NamedExpression } from "@aidkitorg/types/lib/survey";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { DataTable } from "@aidkitorg/component-library";
import { ColumnDef } from "@tanstack/react-table";
import { formatColumnValue } from "./DistroDashboard/DistroDashboardTable";

interface Application {
  uid: string,
  [key: string]: string | string[];
}

interface TableWithApps {
  title: Text,
  columns: (ValueExpr | NamedExpression)[],
  condition: any, // BooleanExpr
  apps: Application[],
}

interface TableConfigurationWithApps {
  tables: TableWithApps[];
}

export function ApplicantIdentities(props: {
  applicant: string,
  path: string,
}) {
  const [currentApplication, setCurrentApplication] = useState<Application>({} as Application);
  const [ungroupedApplications, setUngroupedApplications] = useState([] as Application[]);
  const [title, setTitle] = useState<Text>({} as Text);
  const [tableConfiguration, setTableConfiguration] = useState<TableConfigurationWithApps | undefined>(undefined);
  const getSharedKeyApplicants = usePost("/applicant/get_shared_key_apps");
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
    
  const publicConfig = useContext(PublicConfigurationContext);
  const aOrApplicant = interfaceNumber(publicConfig.interface?.version) > 0 ? 'a' : 'applicant';

  // Helper function to extract field key from ValueExpr or NamedExpression
  // This should match the keys generated by the backend's nameForColumn function
  function getFieldKey(column: ValueExpr | NamedExpression): string | null {
    if (column.kind === 'Named Expression') {
      // For NamedExpression, the name is used as the column key (as returned by nameForColumn)
      // Try current language first, then fall back to English
      const name = column.name?.[context.lang as keyof typeof column.name] || column.name?.['en'];
      if (name) {
        return name;
      }
      // If no name, recursively extract from the expression
      return getFieldKey(column.expression);
    }
    if (column.kind === 'Field') {
      // Handle lookup fields - format matches backend: field-lookupKind-lookupProp
      if (column.lookup) {
        return `${column.field}-${column.lookup.kind}-${column.lookup.prop}`;
      }
      return column.field;
    }
    // For Last Modified Date
    if (column.kind === 'Last Modified Date') {
      return `${column.field} (Last Modified)`;
    }
    // For other ValueExpr types that have a field property
    if ('field' in column && typeof column.field === 'string') {
      return column.field;
    }
    return null;
  }

  // Helper function to get column header text
  function getColumnHeader(column: ValueExpr | NamedExpression): string {
    if (column.kind === 'Named Expression') {
      const name = column.name?.[context.lang as keyof typeof column.name] || column.name?.['en'];
      if (name) return name;
      // Fallback to field key
      const key = getFieldKey(column);
      return key ? snakeToEnglish(key) : 'Column';
    }
    const key = getFieldKey(column);
    return key ? snakeToEnglish(key) : 'Column';
  }

  // Create column definitions for a specific table based on its column configuration
  const createTableColumns = (tableColumns: (ValueExpr | NamedExpression)[]): ColumnDef<Application>[] => {
    const cols: ColumnDef<Application>[] = tableColumns.map((column, index) => {
      const fieldKey = getFieldKey(column);
      const header = getColumnHeader(column);
      
      return {
        id: fieldKey || `col-${index}`,
        accessorKey: fieldKey || `col-${index}`,
        header: header,
        cell: ({ getValue }) => {
          const value = getValue();
          const { content, JSONContent } = formatColumnValue(value);
          return <span className="text-sm text-gray-700">{JSONContent ? <pre className="">{JSON.stringify(JSONContent, undefined, 2)}</pre> : <span>{content}</span>}</span>;
        },
      };
    });

    // Add "View Applicant" column at the end
    cols.push({
      id: 'view_applicant',
      header: 'View',
      enableSorting: false,
      minSize: 20,
      maxSize: 20,
      
      meta: {
        cellProps: {
          className: 'text-right'
        }
      },
      cell: ({ row }) => (
        <div className="flex">
          <a 
            href={`/${aOrApplicant}/${row.original.uid}`} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-blue-600 hover:text-blue-800 inline-block"
          >
            <ArrowTopRightOnSquareIcon className="h-5 w-5" />
          </a>
        </div>
      ),
    });

    return cols;
  };

  // Get tables with apps from tableConfiguration (pre-grouped by backend)
  const tablesWithApps = useMemo(() => {
    if (!tableConfiguration?.tables || tableConfiguration.tables.length === 0) {
      return null;
    }

    // Filter to only tables that have apps
    return tableConfiguration.tables.filter(table => table.apps && table.apps.length > 0);
  }, [tableConfiguration]);

  useEffect(() => {
    (async () => {
      const res = await getSharedKeyApplicants({
        applicant: props.applicant,
        path: props.path
      });

      setTitle(res.title as Text);
      setTableConfiguration(res.tableConfiguration);

      // Find current application - it could be in apps array
      const currApp = res.apps.find((a: Application) => a.uid === props.applicant);
      setCurrentApplication(currApp || {} as Application);

      // Handle ungrouped apps - calculate shared values with current application
      if (res.apps && res.apps.length > 0) {
        let otherApps = res.apps.filter(a => a.uid !== props.applicant);
        const withSharedVals = otherApps.map(a => {
          const sharedVals : string[] = (Object.keys(a).map(k => currApp && currApp[k] === a[k] ? k : '')).filter(f => !!f);

          return {
            ...a,
            shared_vals: sharedVals
          }
        });
        setUngroupedApplications(withSharedVals);
      } else {
        setUngroupedApplications([]);
      }
    })()
  }, [props.applicant, props.path]);

  return (
    <div className="bg-gray-100 -mx-6 px-8 py-8 mb-3 min-h-screen">
      <header className="mb-8">
        <div className="max-w-7xl mx-auto py-2 px-2 sm:px-6 lg:px-8 border-t-0 border-l-0 border-r-0 border-b-3 border-solid border-gray-300 gap-2 sm:gap-4">
          <div className="text-4xl mt-2 ml-4 text-black font-medium">
            {title[context.lang as keyof typeof title]}
          </div>
        </div>
      </header>

      <h4 className="m-4">{L.app_identities.current_application}</h4>
      {currentApplication && 
        <div className="flex items-center justify-between m-4 rounded-lg shadow bg-white ">
          <div className="w-full ">
            <div className="flex items-start gap-x-3 bg-gray-50 p-3 rounded-lg">
              <span className="font-semibold" >{L.app_identities.application_uid}: <a href={`/${aOrApplicant}/${currentApplication.uid}`}>{currentApplication.uid}</a></span>
            </div>
            <div className="mt-3">
              {Object.keys(currentApplication).map(key => {
                const { content, JSONContent } = formatColumnValue(currentApplication[key]);
                if (key !== 'uid') {
                  return (
                    <div key={key} className="mt-1 leading-5 text-gray-700 px-3">
                      <p className="whitespace-nowrap">
                        <span className="font-semibold">{snakeToEnglish(key)}:</span> {JSONContent ? <pre className="">{JSON.stringify(JSONContent, undefined, 2)}</pre> : <span>{content}</span>}
                      </p>
                    </div>)
                }
              })}
            </div>
          </div>
        </div>}
      <h4 className="mt-8 ml-4">{L.app_identities.matched_applications}</h4>
      
      {/* First show ungrouped apps (not in any table) */}
      {ungroupedApplications && ungroupedApplications.length > 0 && (
        <ul role="list">
          {ungroupedApplications.map((app) => (
            <li key={app.uid}>
              <div className="flex items-center justify-between m-4 rounded-lg shadow bg-white ">
                <div className="w-full ">
                  <div className="flex items-start gap-x-3 bg-gray-50 p-3 rounded-lg">
                    <span className="font-semibold" >{L.app_identities.application_uid}: <a href={`/${aOrApplicant}/${app.uid}`}>{app.uid}</a></span>
                  </div>
                  <div className="flex justify-between">
                    <div className="mt-3">
                      {Object.keys(app).map(key => {
                        if (key !== 'uid' && key !== 'shared_vals') {
                          const { content, JSONContent } = formatColumnValue(app[key]);
                          return (
                            <div key={key} className="mt-1 leading-5 text-gray-700 px-3">
                              <p className="whitespace-nowrap">
                                <span className="font-semibold">{snakeToEnglish(key)}:</span> {JSONContent ? <pre className="">{JSON.stringify(JSONContent, undefined, 2)}</pre> : <span>{content}</span>}
                              </p>
                            </div>)
                        }
                      })}
                    </div>
                    {(Array.isArray(app['shared_vals']) && app['shared_vals'].length > 0) && 
                                  <div className="border-t-0 border-r-0 border-b-0 border-l-1 border-solid border-gray-100 rounded my-2">
                                    <div className="px-4 font-semibold" >{L.app_identities.overlap_with_current_app}:</div>
                                    {(app['shared_vals']).map((key, idx) => {
                                      return (
                                        <div key={idx} className="mt-1 leading-5 text-gray-700 px-4">
                                          <span className="">{snakeToEnglish(key)}</span>
                                        </div>)
                                    })}
                                  </div>}
                  </div>

                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Then show tables (pre-grouped by backend) */}
      {tablesWithApps && tablesWithApps.length > 0 && (
        tablesWithApps.map((table, tableIndex) => {
          // Get the appropriate columns for this table (columns are always defined in tableConfiguration)
          const tableColumns = table.columns
            ? createTableColumns(table.columns)
            : [];

          return (
            <div key={tableIndex} className="mb-8">
              {Object.keys(table.title).length > 0 && (
                <h5 className="mt-4 mb-4 ml-4 text-xl font-semibold">
                  {table.title[context.lang as keyof typeof table.title] || table.title['en'] || ''}
                </h5>
              )}
              <div className="flex items-center justify-between m-4">
                <div className="w-full">
                  <DataTable
                    columns={tableColumns}
                    data={table.apps || []}
                    getRowId={(row) => row.uid}
                    showPagination={false}
                    enableSearch={false}
                    className="bg-white rounded-lg shadow"
                  />
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}